/** Signed in, in a workspace, data loaded and syncing; otherwise redirect or wait. */
import { MoonPhases } from "@/components/brand";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/hooks";
import { sync } from "@/lib/sync";
import { useQuery } from "@tanstack/react-query";
import { rememberReturnTo, takeReturnTo } from "@/lib/return-to";
import { Navigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

export { Logo } from "@/components/brand";

export function Splash({ message = "Loading Dayax…" }: { message?: string }) {
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-sidebar text-muted-foreground">
      <MoonPhases className="size-12" />
      <span className="text-[13px]">{message}</span>
    </div>
  );
}

export function AppGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const signedIn = !!me.data;
  const member = !!me.data?.membership;
  const boot = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => sync.bootstrap(),
    enabled: signedIn && member,
    staleTime: Infinity,
    retry: 2,
  });
  const router = useRouter();
  useEffect(() => {
    if (!boot.isSuccess) return;
    sync.start();
    // Back to the link that sent someone to sign in.
    const to = takeReturnTo();
    if (to && to !== location.pathname + location.search) router.history.replace(to);
  }, [boot.isSuccess]);

  if (me.isPending) return <Splash />;
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) return <ToLogin />;
    return <Splash message={`Can't reach Dayax: ${(me.error as Error).message}`} />;
  }
  if (!member) return <Navigate to="/onboarding" />;
  if (boot.isPending) return <Splash message="Loading your workspace…" />;
  if (boot.error) return <Splash message={`Couldn't load the workspace: ${(boot.error as Error).message}`} />;
  return <>{children}</>;
}

function ToLogin() {
  useState(() => rememberReturnTo(location.pathname + location.search + location.hash));
  return <Navigate to="/login" />;
}
